import { useDraftNavigation } from './draft-navigation';
import { CatalogSettings } from './catalog-settings';
import { SourceOrigins } from './source-origins';
import { ConfigurationPreview } from './configuration-preview';
import { AppointmentServices } from './appointment-services';
import { ModelSettings } from './model-settings';
import { LiveSummary } from './live-summary';
import { ClientMemory } from './memory';
import { SalesPlaybookEditor } from './sales-playbook';
import { useState, useRef, type JSX } from 'react';
import { KnowledgeImport } from './knowledge-import';
import { z } from 'zod';
import {
  configurationDraftSchema,
  assistantConfigurationSchema,
  SALES_TECHNIQUES,
  storedConfigurationDraftSchema,
  resolveResponseModel,
  calendarAppointmentKind,
  generateUuidV7,
  calendarWorkingHours,
  formatExactMoney,
  OFFICE_RECONNECTION_REQUIRED_CODES,
  officeProviderSchema,
  type AssistantConfiguration,
  type ConfigurationDraft,
  type BotScope,
} from '@cryptology-assist/domain';
import {
  ApiError,
  botPath,
  idSchema,
  knowledgeSchema,
  request,
  savedSchema,
  usageSchema,
  type Session,
  type Snapshot,
} from './api';
import { useRemote } from './hooks';
import { ErrorNotice, Field, onSubmit, useMutation } from './components';
import {
  dateLabel,
  numberLabel,
  statusLabel,
  type Copy,
  type Language,
} from './copy';

export interface SettingsProps {
  scope: BotScope;
  session: Session;
  copy: Copy;
  language: Language;
  canAdmin: boolean;
  isOwner: boolean;
  snapshot: Snapshot;
  reload: () => void;
}

const repliesResponseSchema = z.object({
  replies: z.array(
    z.object({
      id: z.string(),
      question_text: z.string().nullable(),
      answer_text: z.string(),
      locale: z.string(),
      expires_at: z.string(),
    })
  ),
});

const adoptionResultSchema = z.object({
  adopted: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  skippedBindings: z.number().int().nonnegative(),
  skippedOperations: z.number().int().nonnegative(),
});

export function ConfigurationEditor({
  scope,
  session,
  copy,
  language,
  canAdmin,
  snapshot,
  reload,
}: SettingsProps): JSX.Element {
  const sections = [
    {
      id: 'business',
      name: copy.setupBusiness,
      help: copy.setupBusinessHelp,
    },
    {
      id: 'information',
      name: copy.setupInformation,
      help: copy.setupInformationHelp,
    },
    { id: 'voice', name: copy.setupVoice, help: copy.setupVoiceHelp },
    { id: 'model', name: copy.setupModel, help: copy.setupModelHelp },
    {
      id: 'appointments',
      name: copy.setupAppointments,
      help: copy.setupAppointmentsHelp,
    },
    { id: 'channels', name: copy.setupChannels, help: copy.setupChannelsHelp },
    { id: 'review', name: copy.setupReview, help: copy.setupReviewHelp },
  ] as const;
  const lastStep = sections.length - 1;
  const [step, setStep] = useState(() => {
    const requested = Number(
      new URLSearchParams(window.location.search).get('step') ?? 0
    );
    return Number.isInteger(requested) &&
      requested >= 0 &&
      requested <= lastStep
      ? requested
      : 0;
  });
  const section = sections[step]!.id;
  const revision = useRef(snapshot.draft?.revision ?? null);
  const baseReleaseId = useRef(snapshot.release?.id ?? null);
  const [savedDraft, setSavedDraft] = useState(snapshot.draft);
  const [saveError, setSaveError] = useState<Error | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState<{
    at: string;
    configuration: ConfigurationDraft;
  } | null>(null);
  const saving = useRef<Promise<void> | null>(null);
  const editVersion = useRef(0);
  const latestSave = useRef<() => Promise<void>>(saveDraft);
  function markDirty() {
    editVersion.current++;
    setDirty(true);
  }
  const previous =
    snapshot.draft?.configuration ?? snapshot.release?.configuration_json;
  const [name, setName] = useState(previous?.profile.assistantName ?? ''),
    [businessName, setBusinessName] = useState(
      previous?.profile.businessName ?? ''
    );
  const [locale, setLocale] = useState<'es-PE' | 'en-US'>(
      previous?.profile.locale ?? 'es-PE'
    ),
    [formality, setFormality] = useState<'formal' | 'conversational'>(
      previous?.profile.formality ?? 'conversational'
    );
  const [responseModel, setResponseModel] = useState(
    resolveResponseModel(previous?.responseModel)
  );
  const [leadDestination, setLeadDestination] = useState<'assist' | 'business'>(
    previous?.leadDestination ??
      (previous?.businessConnectionId ? 'business' : 'assist')
  );
  const [captureLeads, setCaptureLeads] = useState(
    previous?.commercialOutcomes?.includes('qualified_lead') ??
      previous?.capabilities.includes('sales') ??
      false
  );
  const [salesPlaybook, setSalesPlaybook] = useState(
    previous?.salesPlaybook ?? {
      version: 1 as const,
      goal: 'wanted_sales_appointment' as const,
      positioning: '',
      motivations: [],
      benefits: [],
      concerns: [],
      adviserOutcomes: [],
      enabledTechniques: SALES_TECHNIQUES.map((item) => item.id),
      excludedClaims: [],
    }
  );
  const [description, setDescription] = useState(
    previous?.business?.description ?? ''
  );
  const [audience, setAudience] = useState(previous?.business?.audience ?? '');
  const [dirty, setDirty] = useState(false);
  const [instructions, setInstructions] = useState(
      previous?.profile.instructions ?? ''
    ),
    [personality, setPersonality] = useState(
      previous?.profile.personality ?? ''
    ),
    [signature, setSignature] = useState(previous?.profile.signature ?? '');
  const [capabilities, setCapabilities] = useState<
    AssistantConfiguration['capabilities']
  >(previous?.capabilities ?? []);
  const [maxCatalogCards, setMaxCatalogCards] = useState(
    previous?.channels.chat.maxCatalogCards ?? 3
  );
  const [chat, setChat] = useState(previous?.channels.chat.enabled ?? false),
    [email, setEmail] = useState(previous?.channels.email.enabled ?? false),
    [origins, setOrigins] = useState(
      previous?.channels.chat.allowedOrigins.join('\n') ?? ''
    );
  const [presentation, setPresentationValue] = useState(
    previous?.channels.chat.presentation
  );
  function setPresentation(value: typeof presentation) {
    setPresentationValue(value);
    markDirty();
  }
  const [emailMode, setEmailMode] = useState<'draft' | 'automatic'>(
    previous?.channels.email.mode ?? 'draft'
  );
  const [appointmentServices, setAppointmentServices] = useState<
    NonNullable<ConfigurationDraft['appointmentServices']>
  >(
    () =>
      previous?.appointmentServices ??
      (previous?.calendar
        ? [
            {
              id: generateUuidV7(),
              name:
                calendarAppointmentKind(previous.calendar) === 'sales_call'
                  ? copy.salesCall
                  : calendarAppointmentKind(previous.calendar) === 'site_visit'
                    ? copy.siteVisit
                    : copy.appointment,
              location: previous.calendar.location ?? '',
              calendar: {
                ...('pool' in previous.calendar
                  ? { pool: previous.calendar.pool }
                  : {
                      connectionId: previous.calendar.connectionId,
                      calendarId: previous.calendar.calendarId,
                    }),
                timezone: previous.calendar.timezone,
                durationMinutes: previous.calendar.durationMinutes,
                appointmentKind: calendarAppointmentKind(previous.calendar),
                weeklyHours: calendarWorkingHours(previous.calendar),
              },
            },
          ]
        : [])
  );
  const [catalog, setCatalog] = useState(previous?.catalog);
  const [business, setBusiness] = useState(
    previous?.businessConnectionId ?? ''
  );
  const [adoptionResult, setAdoptionResult] = useState<z.infer<
    typeof adoptionResultSchema
  > | null>(null);
  const mutation = useMutation();
  const embedCode = `import { mountAssistWidget } from '${window.location.origin}/widget.js';\nmountAssistWidget({ botId: '${scope.botId}', apiBaseUrl: '${window.location.origin}', websiteOrigin: window.location.origin, locale: '${locale}' });`;
  function saveDraft(): Promise<void> {
    if (saving.current) return saving.current;
    const version = editVersion.current;
    setSaveError(null);
    const work = (async () => {
      const commercialOutcomes = [
        ...(captureLeads || appointmentServices.length > 0
          ? (['qualified_lead'] as const)
          : []),
        ...(appointmentServices.length > 0 ? (['appointment'] as const) : []),
      ];
      const salesEnabled = commercialOutcomes.length > 0;
      const configuredCapabilities: AssistantConfiguration['capabilities'] = [
        ...capabilities.filter((capability) => capability !== 'sales'),
        ...(salesEnabled ? (['sales'] as const) : []),
      ];
      const config = configurationDraftSchema.safeParse({
        ...previous,
        leadDestination,
        business:
          description.trim() || audience.trim()
            ? { description, audience }
            : undefined,
        profile: {
          assistantName: name,
          businessName,
          locale,
          formality,
          ...(personality.trim() ? { personality: personality.trim() } : {}),
          instructions,
          signature,
        },
        responseModel,
        capabilities: configuredCapabilities,
        commercialOutcomes,
        ...(salesEnabled
          ? {
              salesPlaybook: {
                ...salesPlaybook,
                goal:
                  appointmentServices.length > 0
                    ? ('wanted_sales_appointment' as const)
                    : ('qualified_lead' as const),
              },
            }
          : { salesPlaybook: undefined }),
        channels: {
          chat: {
            enabled: chat,
            maxCatalogCards,
            ...(presentation ? { presentation } : {}),
            allowedOrigins: origins
              .split('\n')
              .map((value) => value.trim())
              .filter(Boolean),
          },
          email: { enabled: email, mode: emailMode },
        },
        calendar: null,
        appointmentServices,
        businessConnectionId: business || null,
        catalog:
          business && capabilities.includes('offerings') ? catalog : undefined,
      });
      if (!config.success)
        throw new ApiError('CONFIGURATION_INVALID', 400, [
          ...new Set(config.error.issues.map((issue) => issue.path.join('.'))),
        ]);
      const saved = await request(
        botPath(scope, 'configuration/draft'),
        storedConfigurationDraftSchema,
        {
          csrf: session.csrf,
          method: 'POST',
          body: {
            configuration: config.data,
            expectedRevision: revision.current,
            baseReleaseId: baseReleaseId.current,
          },
        }
      );
      revision.current = saved.revision;
      setSavedDraft(saved);
      if (editVersion.current === version) setDirty(false);
      reload();
    })()
      .catch((error: unknown) => {
        const failure =
          error instanceof Error ? error : new Error('REQUEST_FAILED');
        setSaveError(failure);
        throw failure;
      })
      .finally(() => {
        saving.current = null;
      })
      .then(async () => {
        if (editVersion.current !== version) await latestSave.current();
      });
    saving.current = work;
    return work;
  }
  latestSave.current = saveDraft;
  useDraftNavigation(dirty, saveDraft);
  async function goToStep(index: number) {
    if (dirty) await saveDraft();
    setStep(index);
    const url = new URL(window.location.href);
    url.searchParams.set('step', String(index));
    window.history.replaceState(null, '', url);
    window.dispatchEvent(new Event('assist:navigation'));
  }
  async function publishDraft(draft: NonNullable<typeof savedDraft>) {
    setPublishing(true);
    try {
      const release = await request(
        botPath(scope, 'configuration/publish'),
        z.object({ id: z.string(), created_at: z.string() }),
        { csrf: session.csrf, body: { expectedRevision: draft.revision } }
      );
      baseReleaseId.current = release.id;
      setSavedDraft(null);
      revision.current = null;
      setPublished({
        at: release.created_at,
        configuration: draft.configuration,
      });
      reload();
    } finally {
      setPublishing(false);
    }
  }
  // What this editor published is live before the workspace has reloaded.
  const live =
    published &&
    (!snapshot.release ||
      Date.parse(snapshot.release.created_at) < Date.parse(published.at))
      ? published
      : snapshot.release
        ? {
            at: snapshot.release.created_at,
            configuration: snapshot.release.configuration_json,
          }
        : null;
  // A first installation follows the steps. A published assistant is edited in
  // place: its sections are a list, and the next thing to do is to save the
  // change and then to publish it.
  const emphasis =
    !live && step < lastStep
      ? 'next'
      : dirty
        ? 'save'
        : savedDraft
          ? 'publish'
          : null;
  return (
    <div className="settings-content">
      <div className="section-heading">
        <h1>{copy.configuration}</h1>
        {!live && <p>{copy.configurationHelp}</p>}
      </div>
      {live && (
        <LiveSummary
          live={live}
          draft={savedDraft?.configuration ?? null}
          names={Object.fromEntries(
            sections.map((entry) => [entry.id, entry.name])
          )}
          onOpen={(id) =>
            void mutation.run(() =>
              goToStep(sections.findIndex((entry) => entry.id === id))
            )
          }
          copy={copy}
          language={language}
        />
      )}
      <div className={live ? 'editor-layout settings' : 'editor-layout'}>
        <nav className="setup-steps" aria-label={copy.setupNavigation}>
          {sections.map((entry, index) => (
            <button
              key={entry.id}
              type="button"
              aria-current={step !== index ? undefined : live ? 'true' : 'step'}
              onClick={() => void mutation.run(() => goToStep(index))}
            >
              {!live && <span aria-hidden="true">{index + 1}</span>}
              {entry.name}
            </button>
          ))}
        </nav>
        <div className="editor-section">
          <p className="step-help">{sections[step]!.help}</p>
          <form
            id="assistant-configuration"
            noValidate
            onChange={markDirty}
            onSubmit={(event) => onSubmit(event, () => mutation.run(saveDraft))}
          >
            <fieldset disabled={!canAdmin || mutation.busy}>
              <section className="setup-panel" hidden={section !== 'business'}>
                <h2>{copy.businessIdentity}</h2>
                <div className="form-grid">
                  <Field label={copy.name}>
                    <input
                      required
                      maxLength={100}
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                    />
                  </Field>
                  <Field label={copy.businessName}>
                    <input
                      required
                      maxLength={200}
                      value={businessName}
                      onChange={(event) => setBusinessName(event.target.value)}
                    />
                  </Field>
                  <Field label={copy.locale}>
                    <select
                      value={locale}
                      onChange={(event) =>
                        setLocale(
                          event.target.value === 'es-PE' ? 'es-PE' : 'en-US'
                        )
                      }
                    >
                      <option value="es-PE">Español (Perú)</option>
                      <option value="en-US">English</option>
                    </select>
                  </Field>
                  <Field label={copy.formality}>
                    <select
                      value={formality}
                      onChange={(event) =>
                        setFormality(
                          event.target.value === 'formal'
                            ? 'formal'
                            : 'conversational'
                        )
                      }
                    >
                      <option value="conversational">
                        {copy.conversational}
                      </option>
                      <option value="formal">{copy.formal}</option>
                    </select>
                  </Field>
                </div>
                <Field label={copy.businessDescription}>
                  <textarea
                    rows={3}
                    maxLength={2000}
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                  />
                </Field>
                <Field label={copy.businessAudience}>
                  <textarea
                    rows={3}
                    maxLength={2000}
                    value={audience}
                    onChange={(event) => setAudience(event.target.value)}
                  />
                </Field>
              </section>
              <section
                className="setup-panel"
                hidden={section !== 'information'}
              >
                <h2>{copy.configurationSources}</h2>
                <p>{copy.configurationSourcesHelp}</p>
                <p>
                  <a href={`?botId=${scope.botId}&view=knowledge`}>
                    {copy.knowledge}
                  </a>{' '}
                  ·{' '}
                  <a href={`?botId=${scope.botId}&view=connections`}>
                    {copy.connections}
                  </a>
                </p>
                <Field label={copy.businessConnection}>
                  <select
                    value={business}
                    onChange={(event) => setBusiness(event.target.value)}
                  >
                    <option value="">{copy.noBusinessConnection}</option>
                    {snapshot.businessConnections
                      .filter((item) => item.status === 'active')
                      .map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.provider} · {new URL(item.endpoint).hostname}
                        </option>
                      ))}
                  </select>
                </Field>
              </section>
              <section className="setup-panel" hidden={section !== 'business'}>
                <h2>{copy.capabilities}</h2>
                <div className="checks">
                  {(['offerings', 'collections'] as const).map((capability) => (
                    <label key={capability}>
                      <input
                        type="checkbox"
                        checked={capabilities.includes(capability)}
                        onChange={(event) =>
                          setCapabilities(
                            event.target.checked
                              ? [...capabilities, capability]
                              : capabilities.filter(
                                  (value) => value !== capability
                                )
                          )
                        }
                      />
                      {copy[capability]}
                    </label>
                  ))}
                  <label>
                    <input
                      type="checkbox"
                      checked={captureLeads || appointmentServices.length > 0}
                      disabled={appointmentServices.length > 0}
                      onChange={(event) =>
                        setCaptureLeads(event.target.checked)
                      }
                    />
                    {copy.qualifiedLeadOutcome}
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={appointmentServices.length > 0}
                      readOnly
                    />
                    {copy.appointmentOutcome}
                  </label>
                  <label>
                    <input type="checkbox" checked={false} disabled />
                    {copy.productSaleOutcome}
                  </label>
                </div>
                {appointmentServices.length > 0 && (
                  <p className="small muted">{copy.appointmentRequiresLead}</p>
                )}
                {(captureLeads || appointmentServices.length > 0) && (
                  <Field label={copy.leadDestination}>
                    <select
                      value={leadDestination}
                      onChange={(event) =>
                        setLeadDestination(
                          event.target.value === 'business'
                            ? 'business'
                            : 'assist'
                        )
                      }
                    >
                      <option value="assist">{copy.leadAssist}</option>
                      <option value="business">{copy.leadBusiness}</option>
                    </select>
                  </Field>
                )}
              </section>
              <section
                className="setup-panel"
                hidden={section !== 'appointments'}
              >
                <AppointmentServices
                  scope={scope}
                  value={appointmentServices}
                  onChange={(value) => {
                    setAppointmentServices(value);
                    markDirty();
                  }}
                  connections={snapshot.connections}
                  copy={copy}
                  locale={locale}
                />
              </section>
              <section className="setup-panel" hidden={section !== 'voice'}>
                <h2>{copy.personalityAndSales}</h2>
                <Field label={copy.personality}>
                  <textarea
                    value={personality}
                    maxLength={800}
                    rows={3}
                    onChange={(event) => setPersonality(event.target.value)}
                  />
                </Field>
                <p className="small muted">{copy.personalityHelp}</p>
                {(captureLeads || appointmentServices.length > 0) && (
                  <SalesPlaybookEditor
                    scope={scope}
                    value={salesPlaybook}
                    onChange={(value) => {
                      if (value) setSalesPlaybook(value);
                      markDirty();
                    }}
                    copy={copy}
                  />
                )}
                <details className="advanced-settings">
                  <summary>{copy.additionalVoiceRules}</summary>
                  <p className="small muted">{copy.additionalVoiceRulesHelp}</p>
                  <Field label={copy.instructions}>
                    <textarea
                      value={instructions}
                      maxLength={4000}
                      rows={4}
                      onChange={(event) => setInstructions(event.target.value)}
                    />
                  </Field>
                  {email && (
                    <Field label={copy.signature}>
                      <textarea
                        value={signature}
                        maxLength={500}
                        rows={2}
                        onChange={(event) => setSignature(event.target.value)}
                      />
                    </Field>
                  )}
                </details>
              </section>
              <section className="setup-panel" hidden={section !== 'model'}>
                <ModelSettings
                  scope={scope}
                  value={responseModel}
                  onChange={setResponseModel}
                  copy={copy}
                />
              </section>
              <section
                className="setup-panel"
                hidden={section !== 'information'}
              >
                {business && capabilities.includes('offerings') && (
                  <CatalogSettings
                    value={catalog}
                    onChange={(value) => {
                      setCatalog(value);
                      markDirty();
                    }}
                    copy={copy}
                  />
                )}
              </section>
              <section className="setup-panel" hidden={section !== 'channels'}>
                <h2>{copy.channels}</h2>
                <div className="checks">
                  <label>
                    <input
                      type="checkbox"
                      checked={chat}
                      onChange={(event) => setChat(event.target.checked)}
                    />
                    {copy.enableChat}
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={email}
                      onChange={(event) => setEmail(event.target.checked)}
                    />
                    {copy.enableEmail}
                  </label>
                </div>
                {chat && (
                  <Field label={copy.chatOrigins}>
                    <textarea
                      rows={3}
                      required
                      value={origins}
                      onChange={(event) => setOrigins(event.target.value)}
                    />
                  </Field>
                )}
                {chat && (
                  <section className="form-section">
                    <h3>{copy.chatPresentation}</h3>
                    <Field label={copy.catalogMaxChatCards}>
                      <input
                        type="number"
                        min={1}
                        max={12}
                        required
                        value={maxCatalogCards}
                        onChange={(event) =>
                          setMaxCatalogCards(event.target.valueAsNumber)
                        }
                      />
                    </Field>
                    <label>
                      <input
                        type="checkbox"
                        checked={presentation !== undefined}
                        onChange={(event) =>
                          setPresentation(
                            event.target.checked
                              ? {
                                  subtitle: '',
                                  introduction: '',
                                  suggestions: [],
                                }
                              : undefined
                          )
                        }
                      />
                      {copy.customChatPresentation}
                    </label>
                    {presentation && (
                      <>
                        <Field label={copy.chatSubtitle}>
                          <input
                            required
                            maxLength={200}
                            value={presentation.subtitle}
                            onChange={(event) =>
                              setPresentation({
                                ...presentation,
                                subtitle: event.target.value,
                              })
                            }
                          />
                        </Field>
                        <Field label={copy.chatIntroduction}>
                          <textarea
                            required
                            maxLength={1000}
                            value={presentation.introduction}
                            onChange={(event) =>
                              setPresentation({
                                ...presentation,
                                introduction: event.target.value,
                              })
                            }
                          />
                        </Field>
                        <p>{copy.chatSuggestionsHelp}</p>
                        {presentation.suggestions.map((suggestion, index) => (
                          <div className="suggestion-card" key={suggestion.id}>
                            <Field label={copy.chatSuggestionLabel}>
                              <input
                                required
                                maxLength={80}
                                value={suggestion.label}
                                onChange={(event) =>
                                  setPresentation({
                                    ...presentation,
                                    suggestions: presentation.suggestions.map(
                                      (item, position) =>
                                        position === index
                                          ? {
                                              ...item,
                                              label: event.target.value,
                                            }
                                          : item
                                    ),
                                  })
                                }
                              />
                            </Field>
                            <Field label={copy.chatSuggestionMessage}>
                              <textarea
                                required
                                maxLength={1000}
                                value={suggestion.message}
                                onChange={(event) =>
                                  setPresentation({
                                    ...presentation,
                                    suggestions: presentation.suggestions.map(
                                      (item, position) =>
                                        position === index
                                          ? {
                                              ...item,
                                              message: event.target.value,
                                            }
                                          : item
                                    ),
                                  })
                                }
                              />
                            </Field>
                            <Field label={copy.chatSuggestionAction}>
                              <select
                                value={suggestion.requiredAction}
                                onChange={(event) =>
                                  setPresentation({
                                    ...presentation,
                                    suggestions: presentation.suggestions.map(
                                      (item, position) =>
                                        position === index
                                          ? {
                                              ...item,
                                              requiredAction: event.target
                                                .value as typeof item.requiredAction,
                                            }
                                          : item
                                    ),
                                  })
                                }
                              >
                                <option value="search_offerings">
                                  {copy.offerings}
                                </option>
                                <option value="create_lead">
                                  {copy.sales}
                                </option>
                                <option value="calendar_availability">
                                  {copy.calendar}
                                </option>
                              </select>
                            </Field>
                            <button
                              type="button"
                              onClick={() =>
                                setPresentation({
                                  ...presentation,
                                  suggestions: presentation.suggestions.filter(
                                    (_, position) => position !== index
                                  ),
                                })
                              }
                            >
                              {copy.removeChatSuggestion}
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          disabled={presentation.suggestions.length >= 3}
                          onClick={() =>
                            setPresentation({
                              ...presentation,
                              suggestions: [
                                ...presentation.suggestions,
                                {
                                  id: `suggestion_${generateUuidV7()}`,
                                  label: '',
                                  message: '',
                                  requiredAction: 'search_offerings',
                                },
                              ],
                            })
                          }
                        >
                          {copy.addChatSuggestion}
                        </button>
                      </>
                    )}
                  </section>
                )}
                {email && (
                  <Field label={copy.emailMode}>
                    <select
                      value={emailMode}
                      onChange={(event) =>
                        setEmailMode(
                          event.target.value === 'automatic'
                            ? 'automatic'
                            : 'draft'
                        )
                      }
                    >
                      <option value="draft">{copy.draftMode}</option>
                      <option value="automatic">{copy.automaticMode}</option>
                    </select>
                  </Field>
                )}
              </section>
            </fieldset>
          </form>
          <section hidden={section !== 'review'} className="setup-panel">
            {canAdmin && savedDraft && !dirty && (
              <ConfigurationPreview
                key={savedDraft.revision}
                scope={scope}
                session={session}
                revision={savedDraft.revision}
                copy={copy}
                channels={(['chat', 'email'] as const).filter(
                  (channel) =>
                    savedDraft!.configuration.channels[channel].enabled
                )}
              />
            )}
            {canAdmin && snapshot.release && (
              <section className="form-section">
                <p>{copy.adoptPolicyHelp}</p>
                <button
                  disabled={mutation.busy}
                  onClick={() =>
                    void mutation.run(async () => {
                      setAdoptionResult(
                        await request(
                          botPath(scope, 'configuration/adopt'),
                          adoptionResultSchema,
                          { csrf: session.csrf, body: {} }
                        )
                      );
                    })
                  }
                >
                  {copy.adoptPolicy}
                </button>
                {adoptionResult && (
                  <p role="status">
                    {copy.adoptionApplied}: {adoptionResult.adopted}.{' '}
                    {copy.adoptionKept}: {adoptionResult.skipped}.
                  </p>
                )}
              </section>
            )}
            {snapshot.release && (
              <button
                disabled={mutation.busy}
                onClick={() =>
                  void mutation.run(async () => {
                    const artifact = await request(
                      botPath(scope, 'configuration/export'),
                      z.object({
                        schemaVersion: z.literal(1),
                        scope: z.object({
                          companyId: z.string(),
                          botId: z.string(),
                        }),
                        releaseId: z.string(),
                        publishedAt: z.string(),
                        exportedAt: z.string(),
                        knowledgeRevision: z.string(),
                        policyRevision: z.string(),
                        connections: z.array(
                          z.object({
                            id: z.string(),
                            channel: z.enum(['chat', 'email']),
                            provider: z.enum(['web', 'google', 'microsoft']),
                            status: z.enum([
                              'ready',
                              'syncing',
                              'error',
                              'disabled',
                            ]),
                          })
                        ),
                        configuration: assistantConfigurationSchema,
                      })
                    );
                    const href = URL.createObjectURL(
                      new Blob([JSON.stringify(artifact, null, 2)], {
                        type: 'application/json',
                      })
                    );
                    const link = document.createElement('a');
                    link.href = href;
                    link.download = 'configuration.export.json';
                    link.click();
                    URL.revokeObjectURL(href);
                  })
                }
              >
                {copy.exportPublishedConfiguration}
              </button>
            )}
            {snapshot.release?.configuration_json.channels.chat.enabled && (
              <section className="form-section">
                <h2>{copy.embed}</h2>
                <p className="muted">{copy.embedHelp}</p>
                <pre className="code-sample">{embedCode}</pre>
                <button
                  onClick={() =>
                    void mutation.run(async () => {
                      await window.navigator.clipboard.writeText(embedCode);
                    })
                  }
                >
                  {copy.copy}
                </button>
              </section>
            )}
          </section>
        </div>
      </div>
      <div className="sticky-form-actions editor-actions">
        <ErrorNotice error={saveError ?? mutation.error} copy={copy} />
        {published && !dirty && !savedDraft && (
          <div className="notice success" role="status">
            {copy.configurationPublished}
          </div>
        )}
        <div className="form-actions">
          {!live && step > 0 && (
            <button
              type="button"
              disabled={mutation.busy}
              onClick={() => void mutation.run(() => goToStep(step - 1))}
            >
              {copy.previousStep}
            </button>
          )}
          {!live && step < lastStep && (
            <button
              type="button"
              className={emphasis === 'next' ? 'primary' : undefined}
              disabled={mutation.busy}
              onClick={() => void mutation.run(() => goToStep(step + 1))}
            >
              {copy.nextStep} · {sections[step + 1]!.name}
            </button>
          )}
          <span className="small muted editor-state" role="status">
            {publishing
              ? copy.publishingConfigurationHelp
              : dirty
                ? copy.configurationUnsaved
                : savedDraft
                  ? copy.configurationDraftSaved
                  : live
                    ? `${copy.published} · ${dateLabel(live.at, language)}`
                    : copy.notPublished}
          </span>
          <div className="editor-commit">
            {canAdmin && (
              <button
                form="assistant-configuration"
                className={emphasis === 'save' ? 'primary' : undefined}
                disabled={mutation.busy}
              >
                {copy.saveConfigurationDraft}
              </button>
            )}
            {canAdmin && savedDraft && (
              <button
                type="button"
                className={emphasis === 'publish' ? 'primary' : undefined}
                disabled={mutation.busy || dirty}
                onClick={() =>
                  void mutation.run(() => publishDraft(savedDraft))
                }
              >
                {publishing
                  ? copy.publishingConfiguration
                  : copy.publishConfigurationDraft}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Connections({
  scope,
  session,
  copy,
  language,
  canAdmin,
  snapshot,
  reload,
  apiBase = botPath(scope, ''),
  showSourceOrigins = true,
}: Pick<
  SettingsProps,
  'scope' | 'session' | 'copy' | 'language' | 'canAdmin' | 'reload'
> & {
  snapshot: Pick<
    Snapshot,
    'providers' | 'connections' | 'mailFailures' | 'businessConnections'
  >;
  apiBase?: string;
  showSourceOrigins?: boolean;
}): JSX.Element {
  const [provider, setProvider] = useState<'crm' | 'external'>('external'),
    [endpoint, setEndpoint] = useState(''),
    [token, setToken] = useState('');
  const mutation = useMutation();
  const connect = (provider: 'google' | 'microsoft') =>
    mutation.run(async () => {
      const result = await request(
        `${apiBase}connections/authorize`,
        z.object({ url: z.url() }),
        { csrf: session.csrf, body: { provider } }
      );
      window.location.assign(result.url);
    });
  return (
    <div className="settings-content">
      <div className="section-heading">
        <h1>{copy.connections}</h1>
        <p>{copy.connectionHelp}</p>
      </div>
      {showSourceOrigins && (
        <SourceOrigins
          scope={scope}
          purpose="business"
          session={session}
          copy={copy}
        />
      )}
      <ErrorNotice error={mutation.error} copy={copy} />
      {canAdmin && (
        <div className="connect-actions">
          {snapshot.providers.map((provider) => (
            <button
              key={provider}
              disabled={mutation.busy}
              onClick={() => void connect(provider)}
            >
              {provider === 'google'
                ? copy.connectGoogle
                : copy.connectMicrosoft}
              <span>↗</span>
            </button>
          ))}
          {snapshot.providers.length === 0 && (
            <p role="alert">{copy.noIdentityProviders}</p>
          )}
        </div>
      )}
      <section className="form-section">
        <h2>{copy.mailboxes}</h2>
        {snapshot.connections.filter(
          (connection) => connection.channel === 'email'
        ).length === 0 && <p className="muted">{copy.noConnections}</p>}
        {snapshot.connections
          .filter((connection) => connection.channel === 'email')
          .map((connection) => {
            const provider = officeProviderSchema.safeParse(
              connection.provider
            );
            // Only the provider's authorization replaces a refused grant.
            const reconnect =
              provider.success &&
              OFFICE_RECONNECTION_REQUIRED_CODES.includes(
                connection.error ?? ''
              )
                ? provider.data
                : null;
            return (
              <div className="connection-row" key={connection.id}>
                <div>
                  <strong>{connection.address}</strong>

                  <p>
                    {connection.provider} ·{' '}
                    {statusLabel(copy, connection.status)}
                  </p>
                  <span className="small muted">
                    {copy.lastSync}:{' '}
                    {connection.lastSyncedAt
                      ? dateLabel(connection.lastSyncedAt, language)
                      : copy.notSynced}
                  </span>
                  {connection.error && (
                    <p className="error-text">{connection.error}</p>
                  )}
                  {reconnect && (
                    <p role="alert">{copy.calendarReconnectHelp}</p>
                  )}
                </div>
                {canAdmin && reconnect && (
                  <button
                    disabled={mutation.busy}
                    onClick={() => void connect(reconnect)}
                  >
                    {copy.reconnectCalendar}
                  </button>
                )}
                {canAdmin && !reconnect && (
                  <button
                    disabled={mutation.busy}
                    onClick={() =>
                      void mutation.run(async () => {
                        await request(
                          `${apiBase}connections/${connection.id}/status`,
                          savedSchema,
                          {
                            csrf: session.csrf,
                            body: {
                              status:
                                connection.status === 'disabled' ||
                                connection.status === 'error'
                                  ? 'ready'
                                  : 'disabled',
                            },
                          }
                        );
                        reload();
                      })
                    }
                  >
                    {connection.status === 'disabled' ||
                    connection.status === 'error'
                      ? copy.enable
                      : copy.disable}
                  </button>
                )}
              </div>
            );
          })}
      </section>
      {snapshot.mailFailures.length > 0 && (
        <section className="form-section">
          <h2>{copy.mailImportFailures}</h2>
          <p>{copy.mailImportHelp}</p>
          {snapshot.mailFailures.map((failure) => (
            <div className="connection-row" key={failure.id}>
              <div>
                <strong>
                  {snapshot.connections.find(
                    (connection) => connection.id === failure.connection_id
                  )?.address ?? failure.connection_id}
                </strong>
                <p className="error-text">{failure.error_code}</p>
                <code>{failure.provider_message_id}</code>
                <p className="small muted">
                  {dateLabel(failure.last_seen_at, language)}
                </p>
              </div>
              {canAdmin && (
                <button
                  disabled={mutation.busy}
                  onClick={() =>
                    void mutation.run(async () => {
                      await request(
                        `${apiBase}mail-failures/${failure.id}`,
                        savedSchema,
                        { csrf: session.csrf, body: {} }
                      );
                      reload();
                    })
                  }
                >
                  {copy.retry}
                </button>
              )}
            </div>
          ))}
        </section>
      )}
      <section className="form-section">
        <h2>{copy.businessSystems}</h2>
        {snapshot.businessConnections.map((connection) => (
          <div className="connection-row" key={connection.id}>
            <strong>{connection.provider}</strong>
            <span>{new URL(connection.endpoint).hostname}</span>
            <span className="badge">
              {statusLabel(copy, connection.status)}
            </span>
            {connection.error_code && (
              <code role="alert">{connection.error_code}</code>
            )}
          </div>
        ))}
        {canAdmin && (
          <form
            onSubmit={(event) =>
              onSubmit(event, () =>
                mutation.run(async () => {
                  await request(`${apiBase}business-connections`, idSchema, {
                    csrf: session.csrf,
                    body: { provider, endpoint, token },
                  });
                  setToken('');
                  reload();
                })
              )
            }
          >
            <div className="form-grid">
              <Field label={copy.provider}>
                <select
                  value={provider}
                  onChange={(event) =>
                    setProvider(
                      event.target.value === 'crm' ? 'crm' : 'external'
                    )
                  }
                >
                  <option value="external">{copy.external}</option>
                  <option value="crm">CRM</option>
                </select>
              </Field>
              <Field label={copy.endpoint}>
                <input
                  type="url"
                  required
                  value={endpoint}
                  onChange={(event) => setEndpoint(event.target.value)}
                />
              </Field>
            </div>
            <Field label={copy.credential}>
              <input
                type="password"
                autoComplete="off"
                required
                minLength={32}
                value={token}
                onChange={(event) => setToken(event.target.value)}
              />
            </Field>
            <button className="primary" disabled={mutation.busy}>
              {copy.connect}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}

export function Knowledge({
  scope,
  session,
  copy,
  language,
  canAdmin,
}: SettingsProps): JSX.Element {
  const sources = useRemote(botPath(scope, 'knowledge'), knowledgeSchema);
  const [refreshSource, setRefreshSource] = useState('');
  const [source, setSource] = useState(''),
    [type, setType] = useState('manual'),
    [content, setContent] = useState('');
  const mutation = useMutation();
  return (
    <div className="settings-content">
      <div className="section-heading">
        <h1>{copy.knowledge}</h1>
        <p>{copy.knowledgeHelp}</p>
      </div>
      <SourceOrigins
        scope={scope}
        purpose="knowledge"
        session={session}
        copy={copy}
      />
      <ErrorNotice error={sources.error} copy={copy} onRetry={sources.reload} />
      {sources.loading && !sources.data && (
        <p className="loading">{copy.loading}</p>
      )}
      {sources.data?.sources.length === 0 && (
        <p className="muted">{copy.noKnowledge}</p>
      )}
      {sources.data?.sources.map((item) => (
        <div className="source-row" key={item.id}>
          <div>
            <strong>{item.canonical_uri}</strong>
            <p>
              {statusLabel(copy, item.source_type)} ·{' '}
              {dateLabel(item.captured_at, language)}
            </p>
          </div>
          <span className="badge">{statusLabel(copy, item.status)}</span>
          {canAdmin && item.source_type === 'website' && (
            <button
              className="quiet"
              onClick={() => setRefreshSource(item.canonical_uri)}
            >
              {copy.refresh}
            </button>
          )}
          {canAdmin && item.status === 'approved' && (
            <button
              className="quiet danger"
              disabled={mutation.busy}
              onClick={() =>
                void mutation.run(async () => {
                  await request(
                    botPath(scope, `knowledge/${item.id}`),
                    savedSchema,
                    { csrf: session.csrf, method: 'DELETE' }
                  );
                  sources.reload();
                })
              }
            >
              {copy.revokeKnowledge}
            </button>
          )}
        </div>
      ))}
      {canAdmin && (
        <KnowledgeImport
          key={refreshSource}
          scope={scope}
          session={session}
          copy={copy}
          onPublished={sources.reload}
          initialSource={refreshSource}
        />
      )}
      {canAdmin && (
        <form
          className="form-section"
          onSubmit={(event) =>
            onSubmit(event, () =>
              mutation.run(async () => {
                await request(botPath(scope, 'knowledge'), idSchema, {
                  csrf: session.csrf,
                  body: { source, type, content },
                });
                setContent('');
                sources.reload();
              })
            )
          }
        >
          <div className="form-grid">
            <Field label={copy.source}>
              <input
                type="url"
                required
                value={source}
                onChange={(event) => setSource(event.target.value)}
              />
            </Field>
            <Field label={copy.sourceType}>
              <select
                value={type}
                onChange={(event) => setType(event.target.value)}
              >
                {(
                  [
                    'manual',
                    'website',
                    'document',
                    'approved_chat_learning',
                  ] as const
                ).map((value) => (
                  <option key={value} value={value}>
                    {copy[value]}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field label={copy.content}>
            <textarea
              required
              rows={12}
              maxLength={50_000}
              value={content}
              onChange={(event) => setContent(event.target.value)}
            />
          </Field>
          <ErrorNotice error={mutation.error} copy={copy} />
          <button className="primary" disabled={mutation.busy}>
            {copy.publish}
          </button>
        </form>
      )}
      <ClientMemory
        scope={scope}
        session={session}
        copy={copy}
        language={language}
        canAdmin={canAdmin}
      />
      <ApprovedReplies
        scope={scope}
        session={session}
        copy={copy}
        language={language}
        canAdmin={canAdmin}
        sources={
          sources.data?.sources.filter((item) => item.status === 'approved') ??
          []
        }
      />
    </div>
  );
}

function ApprovedReplies({
  scope,
  session,
  copy,
  language,
  canAdmin,
  sources,
}: {
  scope: BotScope;
  session: Session;
  copy: Copy;
  language: Language;
  canAdmin: boolean;
  sources: { id: string; canonical_uri: string }[];
}): JSX.Element {
  const replies = useRemote(
    botPath(scope, 'approved-replies'),
    repliesResponseSchema
  );
  const [questions, setQuestions] = useState(''),
    [answer, setAnswer] = useState(''),
    [sourceIds, setSourceIds] = useState<string[]>([]),
    [channel, setChannel] = useState('chat'),
    [locale, setLocale] = useState('es-PE'),
    [expires, setExpires] = useState('');
  const mutation = useMutation();
  return (
    <section className="form-section">
      <h2>{copy.approvedReplies}</h2>
      <p className="muted">{copy.approvedReplyHelp}</p>
      <ErrorNotice error={replies.error} copy={copy} onRetry={replies.reload} />
      {replies.data?.replies.map((reply) => (
        <article className="source-row" key={reply.id}>
          <div>
            <strong>{reply.question_text ?? copy.noQuestion}</strong>
            <p>{reply.answer_text}</p>
            <span>
              {reply.locale} · {dateLabel(reply.expires_at, language)}
            </span>
          </div>
          {canAdmin && (
            <button
              disabled={mutation.busy}
              onClick={() =>
                void mutation.run(async () => {
                  await request(
                    botPath(scope, `approved-replies/${reply.id}`),
                    savedSchema,
                    { method: 'DELETE', csrf: session.csrf }
                  );
                  replies.reload();
                })
              }
            >
              {copy.revoke}
            </button>
          )}
        </article>
      ))}
      {canAdmin && (
        <form
          onSubmit={(event) =>
            onSubmit(event, () =>
              mutation.run(async () => {
                await request(
                  botPath(scope, 'approved-replies'),
                  z.object({ ids: z.array(z.string()) }),
                  {
                    csrf: session.csrf,
                    body: {
                      questions: questions
                        .split('\n')
                        .map((value) => value.trim())
                        .filter(Boolean),
                      answer,
                      sourceIds,
                      channel,
                      locale,
                      factClass: 'static_knowledge',
                      expiresAt: new Date(expires).toISOString(),
                    },
                  }
                );
                setQuestions('');
                setAnswer('');
                replies.reload();
              })
            )
          }
        >
          <Field label={copy.questions}>
            <textarea
              required
              rows={4}
              value={questions}
              onChange={(event) => setQuestions(event.target.value)}
            />
          </Field>
          <Field label={copy.answer}>
            <textarea
              required
              rows={6}
              maxLength={8000}
              value={answer}
              onChange={(event) => setAnswer(event.target.value)}
            />
          </Field>
          <div className="form-grid">
            <Field label={copy.channels}>
              <select
                value={channel}
                onChange={(event) => setChannel(event.target.value)}
              >
                <option value="chat">{copy.chat}</option>
                <option value="email">{copy.email}</option>
              </select>
            </Field>
            <Field label={copy.locale}>
              <select
                value={locale}
                onChange={(event) => setLocale(event.target.value)}
              >
                <option value="es-PE">Español (Perú)</option>
                <option value="en-US">English</option>
              </select>
            </Field>
            <Field label={copy.expires}>
              <input
                required
                type="datetime-local"
                value={expires}
                onChange={(event) => setExpires(event.target.value)}
              />
            </Field>
          </div>
          <fieldset>
            <legend>{copy.source}</legend>
            {sources.map((source) => (
              <label className="field" key={source.id}>
                <span>
                  <input
                    type="checkbox"
                    checked={sourceIds.includes(source.id)}
                    onChange={(event) =>
                      setSourceIds(
                        event.target.checked
                          ? [...sourceIds, source.id]
                          : sourceIds.filter((id) => id !== source.id)
                      )
                    }
                  />
                  {source.canonical_uri}
                </span>
              </label>
            ))}
          </fieldset>
          <button
            className="primary"
            disabled={mutation.busy || sourceIds.length === 0}
          >
            {copy.approve}
          </button>
        </form>
      )}
      <ErrorNotice error={mutation.error} copy={copy} />
    </section>
  );
}

export function Usage({
  scope,
  copy,
  language,
  apiBase = botPath(scope, ''),
}: Pick<SettingsProps, 'scope' | 'copy' | 'language'> & {
  apiBase?: string;
}): JSX.Element {
  const usage = useRemote(`${apiBase}usage`, usageSchema);
  return (
    <section>
      <div className="section-heading">
        <h1>{copy.usage}</h1>
      </div>
      <ErrorNotice error={usage.error} copy={copy} onRetry={usage.reload} />
      {usage.loading && !usage.data && (
        <p className="loading">{copy.loading}</p>
      )}
      {usage.data && (
        <>
          <section>
            <h2>{copy.subscription}</h2>
            <dl className="usage-facts">
              <div>
                <dt>{copy.status}</dt>
                <dd>{statusLabel(copy, usage.data.subscription.status)}</dd>
              </div>
              <div>
                <dt>{copy.period}</dt>
                <dd>
                  {dateLabel(
                    usage.data.subscription.current_period_starts_at,
                    language
                  )}{' '}
                  —{' '}
                  {dateLabel(
                    usage.data.subscription.current_period_ends_at,
                    language
                  )}
                </dd>
              </div>
              <div>
                <dt>{copy.conversationsUsed}</dt>
                <dd>
                  {numberLabel(usage.data.conversations, language)} /{' '}
                  {numberLabel(
                    String(usage.data.subscription.included_conversations),
                    language
                  )}
                </dd>
              </div>
              <div>
                <dt>{copy.monthlyPrice}</dt>
                <dd>
                  {formatExactMoney(
                    BigInt(usage.data.subscription.monthly_price_usd_minor),
                    'USD',
                    2,
                    language === 'es' ? 'es-PE' : 'en-US'
                  )}
                </dd>
              </div>
            </dl>
          </section>
          <section className="form-section">
            <h2>{copy.modelUsage}</h2>
            {usage.data.models.length === 0 ? (
              <p className="muted">{copy.noUsage}</p>
            ) : (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>{copy.model}</th>
                      <th className="numeric">{copy.operations}</th>
                      <th className="numeric">{copy.inputTokens}</th>
                      <th className="numeric">{copy.cachedTokens}</th>
                      <th className="numeric">{copy.outputTokens}</th>
                      <th className="numeric">{copy.providerCost}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usage.data.models.map((model) => (
                      <tr key={model.model}>
                        <td>{model.model}</td>
                        <td className="numeric">
                          {numberLabel(model.operations, language)}
                        </td>
                        <td className="numeric">
                          {numberLabel(model.input_tokens, language)}
                        </td>
                        <td className="numeric">
                          {numberLabel(model.cached_input_tokens, language)}
                        </td>
                        <td className="numeric">
                          {numberLabel(model.output_tokens, language)}
                        </td>
                        <td className="numeric">
                          {formatExactMoney(
                            BigInt(model.provider_cost_micro_usd),
                            'USD',
                            6,
                            language === 'es' ? 'es-PE' : 'en-US'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {usage.data.unpriced_operations !== '0' && (
              <p className="notice">
                {copy.unpriced}: {usage.data.unpriced_operations}
              </p>
            )}
          </section>
        </>
      )}
    </section>
  );
}
